import React, { useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  Body1,
  Caption1,
  Card,
  CardFooter,
  CardPreview,
} from '@fluentui/react-components';
import { ListItem } from '@fluentui/react-list-preview';

import { getTimeDescription } from '@bingads-webui-campaign-react/ads-studio-app-shared-components';
import { Image20Regular } from '@fluentui/react-icons';
import { useImageCardStyles } from '../../../home-page-styles/component-styles/home-page-image-asset-cards';

export const ImageAssetCard = ({
  asset, i18n, navigation, logger, ariaSetsize, ariaPosinset, onClickCard,
}) => {
  const styles = useImageCardStyles();

  const assetName = useMemo(() => (asset.name ? asset.name : asset.id), [asset.id, asset.name]);

  const imageTimeCaption = useMemo(() => {
    if (asset.modifiedTime > asset.createdTime) {
      return getTimeDescription(asset.modifiedTime, i18n) ? `${i18n.getString(_TL_('Edited:'))} ${getTimeDescription(asset.modifiedTime, i18n)}` : '';
    }
    return getTimeDescription(asset.createdTime, i18n) ? `${i18n.getString(_TL_('Uploaded:'))} ${getTimeDescription(asset.createdTime, i18n)}` : '';
  }, [asset.modifiedTime, asset.createdTime, i18n]);

  const handleImageAssetClick = useCallback((imageAsset) => {
    if (logger && logger.logTrace) {
      logger.logTrace('Image Details: edit image', { assetId: imageAsset.id });
    }
    navigation.gotoEditImagePage({ imageId: imageAsset.id });
    onClickCard();
  }, [logger, navigation, onClickCard]);

  const handleImageAssetKeyDown = useCallback((event, imageAsset) => {
    if (event.key === 'Enter') {
      handleImageAssetClick(imageAsset);
      event.preventDefault();
    }
  }, [handleImageAssetClick]);

  return (
    <ListItem
      aria-setsize={ariaSetsize}
      aria-posinset={ariaPosinset}
      aria-label={`Preview of asset ${assetName} ${imageTimeCaption}`}
      onClick={() => handleImageAssetClick(asset)}
      onKeyDown={event => handleImageAssetKeyDown(event, asset)}
      className={styles.imageCardListItem}
    >
      <Card
        className={styles.imageCard}
      >
        <CardPreview>
          <img
            key={asset.id}
            src={asset.url || asset.originalUrl}
            alt={`Preview of asset ${assetName}`}
            className={styles.imageCardThumbnail}
          />
        </CardPreview>
        <div className={styles.imageCardFooter}>
          <CardFooter className={styles.imageCardFooterContent}>
            <div className={styles.imageIconAndName}>
              <Image20Regular style={{ marginRight: '8px', fontSize: 20 }} />
              <Body1 className={styles.imageName} title={assetName}>{assetName}</Body1>
            </div>
            <div className={styles.imageCardCaption}>
              <Caption1>{imageTimeCaption || ''}</Caption1>
            </div>
          </CardFooter>
        </div>
      </Card>
    </ListItem>
  );
};

ImageAssetCard.propTypes = {
  asset: PropTypes.shape({
    name: PropTypes.string,
    createdTime: PropTypes.instanceOf(Date),
    modifiedTime: PropTypes.instanceOf(Date),
    originalUrl: PropTypes.string,
    id: PropTypes.string,
    url: PropTypes.string,
  }).isRequired,
  i18n: PropTypes.shape({}).isRequired,
  navigation: PropTypes.shape({
    gotoEditImagePage: PropTypes.func,
  }).isRequired,
  logger: PropTypes.shape({
    logTrace: PropTypes.func,
  }).isRequired,
};
